// Field constants mirror product-config-admin/src/App.jsx (CATS/GENDERS/DECO/TIERS).
export const CATEGORIES = ['hoodies', 'tees', 'pants', 'shorts', 'hats', 'bags', 'crewnecks', 'quarter-zips'] as const;
export const GENDERS = ['unisex', 'mens', 'womens', 'youth'] as const;
export const DECORATIONS = ['dtf', 'screen-print', 'embroidery', 'sublimation', 'vinyl'] as const;
export const TIERS = ['premium', 'name-brand', 'essentials'] as const;
// Size + print-position sets mirror productconfig-dashboard's product form.
export const ALL_SIZES = ['YS', 'YM', 'YL', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', 'OSFA'] as const;
export const PRINT_POSITIONS = ['full-front', 'left-chest', 'full-chest', 'left-leg'] as const;

// One placed logo zone on a view image, in NATIVE image pixels (the
// convention pc-admin's MockupEditor writes and the mockup pipeline reads).
export type ViewArea = { areaId: string; x: number; y: number; w: number; h: number };

// A named print area (multi-logo product): zones on view images point at one
// of these via areaId. Width/height are print inches; addon is a $ markup.
export type PrintAreaDef = {
  id: string;
  label?: string;
  position?: string;
  printWidth?: number;
  printHeight?: number;
  addon?: number;
};

// views.{front,back,side} — storefront/AI image slots written by supplier-catalog
// (fetch-images, AI standardization). aiStatus: 'generated' | 'approved' | 'rejected'.
// overlay* fields written by supplier-catalog overlay-upload (hoodie drawstring
// overlays) and mirrored back from teamstore-mockups when baseSig drifts.
export type ProductView = {
  imageUrl?: string;
  imageDataUrl?: string;
  imagePath?: string;
  aiStatus?: string;
  areas?: ViewArea[];
  overlayImage?: string;
  overlayBaseSig?: { width: number; height: number; baseImagePath: string; baseGeneration: string };
  overlayApplyToPositions?: string[];
  overlayUploadedAt?: string;
  overlayStale?: boolean;
  overlayStaleReason?: string;
  [k: string]: unknown;
};

export type ProductConfig = {
  id: string;
  displayName?: string;
  productTitle?: string;
  shortName?: string;
  description?: string;
  brandPartner?: string;
  category?: string;
  gender?: string;
  tier?: string;
  tierLabel?: string;
  sortOrder?: number;
  active?: boolean;
  approved?: boolean;
  // Decoration lives both as a canonical map and as legacy top-level fields in
  // the wild — readers fall back, writers keep both in sync.
  decorationMethod?: string;
  decorationDetail?: string;
  leadTimeDays?: number;
  decoration?: { method?: string; detail?: string; leadTime?: number };
  sizes?: string[];
  baseProduct?: {
    styleNumber?: string;
    color?: string;
    colorCode?: string;
    supplier?: string;
    brand?: string;
    fabricComposition?: string;
    [k: string]: unknown;
  };
  pricing?: { basePrice?: number; compareAtPrice?: number; cost?: number; blankCost?: number; currency?: string };
  printArea?: { position?: string; positionDisplay?: string; width?: number; height?: number };
  mockup?: {
    templateFile?: string;
    logoX?: number;
    logoY?: number;
    logoMaxWidth?: number;
    logoMaxHeight?: number;
    templateImageUrl?: string;
    templateImageMissing?: boolean;
    [k: string]: unknown;
  };
  shopify?: { productType?: string; vendor?: string; tags?: string[]; sizeChartUrl?: string };
  views?: { front?: ProductView; back?: ProductView; side?: ProductView };
  printAreas?: PrintAreaDef[];
  [k: string]: unknown;
};

export const VIEW_KEYS = ['front', 'back', 'side'] as const;
export type ViewKey = (typeof VIEW_KEYS)[number];

// Mirrors product-config-admin's genId(cat,color,style,pos): slug from catalog
// identity. Position omitted (admin defaults left-chest 'lc'); collisions just
// upsert the same doc, acceptable for v1.
export function genProductConfigId(p: ProductConfig): string {
  const c = String(p.baseProduct?.color || 'na').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const style = String(p.baseProduct?.styleNumber || 'na').toLowerCase();
  return `ts-${c}-${style}-lc`;
}
